import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/invoice.mutation"

import type {
  CreateInvoiceInput,
  CreateInvoiceMutation,
  CreateInvoiceMutationVariables,
} from "@/graphql/codegen/graphql"
import { CreateInvoiceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateInvoiceReturn {
  createInvoice: (input: CreateInvoiceInput) => Promise<string | null>
  isCreating: boolean
}

export const useCreateInvoice = (): UseCreateInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<CreateInvoiceMutation, CreateInvoiceMutationVariables>(
    CreateInvoiceDocument
  )

  const createInvoice = useCallback(
    async (input: CreateInvoiceInput): Promise<string | null> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.invoice.create
      const hasError = error || output?.__typename === "CreateInvoiceFailure"

      if (hasError) {
        const message = output?.__typename === "CreateInvoiceFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to create invoice. Please try again.",
        })
        return null
      }

      if (output?.__typename !== "CreateInvoiceSuccess") {
        notify({ type: NotificationType.Error, message: "Failed to create invoice. Please try again." })
        return null
      }

      notify({ type: NotificationType.Success, message: "Invoice created" })
      return output.invoice.invoiceId
    },
    [executeMutation, notify]
  )

  return { createInvoice, isCreating: fetching }
}
